<template>
  <CCardBody>
    <!-- Filters -->
    <CCard class="filters" accent-color="warning">
      <CCardHeader role="button" class="text-warning shadow-none card-header" @click="resetFilters">
        <strong class="m-0"><CIcon :name="`cil-filter${$store.state.filterShow ? '-x' : ''}`" /> Filters</strong>
        <div class="card-header-actions">
          <CIcon :name="`cil-chevron-${$store.state.filterShow ? 'bottom' : 'top'}`" />
        </div>
      </CCardHeader>

      <CCollapse :show="$store.state.filterShow">
        <CCardBody class="p-2 d-flex flex-column flex-gap-2">
          <div class="d-flex flex-lg-row flex-column flex-gap-2">
            <!-- Hub Filter -->
            <v-select
              class="v-select-filter w-100"
              placeholder="Hubs.."
              label="name"
              v-model="selectedHubs"
              :options="hubs"
              :searchable="false"
              :reduce="(hub) => hub.id"
              multiple
              @input="inputFilter"
            >
              <template v-slot:option="hub">
                <strong>{{ hub.zip }}</strong> -
                <span>{{ hub.name }} </span>
              </template>
            </v-select>

            <!-- Status Filter -->
            <v-select
              class="v-select-filter w-100"
              placeholder="Status.."
              v-model="selectedStatus"
              :options="[
                { value: 'active', label: 'Is Working' },
                { value: 'inactive', label: 'Is Not Working' },
              ]"
              :searchable="false"
              :reduce="(o) => o.value"
              multiple
              @input="inputFilter"
            />

            <!-- Vehicle Mode Filter -->
            <v-select
              class="v-select-filter w-100"
              placeholder="Vehicle mode.."
              v-model="selectedVehicleMode"
              :options="getTravelModes"
              :reduce="(option) => option.value"
              :searchable="false"
              @input="inputFilter"
            />
          </div>

          <div class="d-flex flex-lg-row flex-column flex-gap-2">
            <!-- Reg Channel Filter -->
            <v-select
              class="v-select-filter w-100"
              placeholder="Reg Channel.."
              v-model="selectedRegChannel"
              :options="[
                { value: 'web', label: 'Website' },
                { value: 'app', label: 'App' },
                { value: 'bo', label: 'BO' },
              ]"
              :searchable="false"
              :reduce="(o) => o.value"
              multiple
              @input="inputFilter"
            />

            <!-- Contract Model Filter -->
            <v-select
              class="v-select-filter w-100"
              placeholder="Contract model.."
              v-model="selectedContractModel"
              :options="[
                { value: 'no_active_contract', label: '- No active contract -' },
                { value: 'per_order', label: 'Per order' },
                { value: 'hourly', label: 'Hourly' },
              ]"
              :reduce="(option) => option.value"
              :searchable="false"
              @input="handleContractModelChange"
            />

            <!-- Employment Type Filter -->
            <v-select
              class="v-select-filter w-100"
              placeholder="Employment type.."
              v-model="selectedEmploymentType"
              :options="employmentTypeOptions"
              :reduce="(option) => option.value"
              :searchable="false"
              @input="inputFilter"
            />

            <!-- Search -->
            <CInput
              class="search w-100 mb-0"
              type="search"
              placeholder="Search for something else ..."
              v-model="search"
              @input="searchFilter"
            />
          </div>

          <fieldset class="border rounded p-1 bg-light">
            <legend class="w-auto bg-light py-1 px-2 c-legend-title">Registration Notes Filter</legend>
            <div class="d-flex flex-lg-row flex-column flex-gap-2">
              <!-- City Filter -->
              <CInput
                class="search mb-0 w-100"
                type="search"
                placeholder="City..."
                v-model="regNotesFilter.selectedCity"
                @input="searchFilter"
              />

              <!-- Flex Hours Filter -->
              <v-select
                class="v-select-filter w-100"
                placeholder="Contract model.."
                v-model="regNotesFilter.isFlexHours"
                :options="[
                  { value: true, label: 'Per order' },
                  { value: false, label: 'Hourly' },
                ]"
                :reduce="(option) => option.value"
                :searchable="false"
                @input="inputFilter"
              />

              <!-- Shift Filter -->
              <v-select
                class="v-select-filter w-100"
                placeholder="Shift..."
                v-model="regNotesFilter.selectedShift"
                :options="[
                  { value: 'weekday', label: 'Weekdays' },
                  { value: 'weekend', label: 'Weekend' },
                  { value: 'flex', label: 'Both weekdays and weekends' },
                ]"
                :reduce="(option) => option.value"
                :searchable="false"
                @input="inputFilter"
              />

              <!-- Vehicle Mode Filter -->
              <v-select
                class="v-select-filter w-100"
                placeholder="Vehicle mode.."
                v-model="regNotesFilter.selectedVehicle"
                :options="getTravelModes"
                :reduce="(option) => option.value"
                :searchable="false"
                @input="inputFilter"
              />
            </div>
          </fieldset>
        </CCardBody>
      </CCollapse>
    </CCard>

    <div class="d-flex justify-content-end mb-3">
      <CButton
        size="sm"
        :color="showMap ? 'info' : 'secondary'"
        :variant="showMap ? '' : 'outline'"
        @click="toggleMap"
      >
        <CIcon name="cil-map" class="mr-1" />
        {{ showMap ? 'Hide Map' : 'Map' }}
      </CButton>
    </div>

    <div v-if="showMap" class="mb-3">
      <div v-if="mapLoading" class="small text-muted mb-1">Refreshing locations...</div>
      <CourierLocationsMap
        :couriers="mapCouriers"
        :hubs="hubs"
        height="520px"
      />
    </div>

    <CDataTable
      striped
      hover
      :items="loadedItems"
      :fields="fields"
      :sorter="{ external: true, resetable: true }"
      :itemsPerPageSelect="{
        external: true,
        values: [5, 15, 25, 50, 100, 250, 500],
      }"
      :items-per-page.sync="itemsPerPage"
      :loading="loading"
      :noItemsView="{
        noResults: 'No filtering results are available!',
        noItems: 'No courier found!',
      }"
      @update:sorter-value="sorterValue"
      @pagination-change="paginationChange"
    >
      <!-- Actions -->
      <template #actions="{ item, index }">
        <td>
          <CButton
            :to="{ name: 'Edit Courier', params: { id: item.id } }"
            size="sm"
            color="info"
            variant="ghost"
            shape="pill"
          >
            <CIcon name="cil-align-left" />
          </CButton>

          <template v-if="item.is_deletable === true">
            <CSpinner v-if="item.deleting" size="sm" color="danger" class="ml-3" />
            <CButton
              v-else
              size="sm"
              color="danger"
              variant="ghost"
              shape="pill"
              @click="deleteItem(item, index)"
            >
              <CIcon name="cil-trash" />
            </CButton>
          </template>

          <CButton
            @click="showInternalNotes(item)"
            v-c-tooltip="{ content: 'Internal Notes', placement: 'right' }"
            size="sm"
            color="warning"
            variant="ghost"
            shape="pill"
          >
            <CIcon name="cil-note-add" />
          </CButton>
        </td>
      </template>

      <!-- ID -->
      <template #id="{ item }">
        <td>#{{ item?.id }}</td>
      </template>

      <!-- User Icon -->
      <template #image_icon="{ item }">
        <td>
          <CImg
            :src="item.image_icon || item.gravatar"
            class="c-avatar-img"
            style="width: 36px; height: 36px"
            placeholderColor="lightgray"
          />
        </td>
      </template>

      <!-- Name -->
      <template #first_name="{ item }">
        <td>
          <div class="vertical-center flex-gap-1 mb-2">
            <strong>{{ item.first_name }}</strong>
          </div>
          <div class="small"><CIcon name="cil-at" class="mr-1" />{{ item.email }}</div>
          <div class="small"><CIcon name="cil-phone" class="mr-1" />{{ item.mobile }}</div>
        </td>
      </template>

      <!-- Contact -->
      <!-- <template #contact="{item}">
        <td>
          <div><CIcon name="cil-at" class="mr-1" />{{ item.email }}</div>
          <div><CIcon name="cil-phone" class="mr-1" />{{ item.mobile }}</div>
        </td>
      </template> -->

      <!-- Contract model -->
      <template #contract_model="{ item }">
        <td>
          <div v-if="item.active_contract">
            <span>{{ item.active_contract.contract_model | normalizeSentence | uppercase }} / </span>
            <span v-if="item.active_contract.employment_type">{{
              item.active_contract.employment_type | normalizeSentence | uppercase
            }}</span>
            <span v-else class="flash-it">❓</span>
          </div>
          <span v-else class="flash-it text-danger">❌ No active contract!</span>
        </td>
      </template>

      <!-- Vehicle -->
      <template #vehicle="{ item }">
        <td>
          <div v-if="item.vehicle" class="d-flex align-items-center flex-gap-2">
            <MIcon :tag="item.vehicle.mode" />
            <span class="text-uppercase">{{ item.vehicle.license_plate }}</span>
          </div>
        </td>
      </template>

      <!-- Hubs -->
      <template #hubs="{ item }">
        <td class="p-0">
          <CIcon 
            v-if="item.settings?.is_dynamic_hub" 
            name="cil-location-pin" 
            class="mr-1 text-info" 
            v-c-tooltip="{ content: 'Dynamic Hub' }" 
          />
          <CIcon v-if="item._classes.includes('table-warning')" name="cil-warning" size="lg" />
          <CBadge v-for="hub in item.hubs" :key="hub.id" class="mx-1"
            v-c-tooltip="{ content: hub.delivery_disabled ? 'Delivery disabled' : 'Delivery enabled' }"
            :color="hub.delivery_disabled ? 'danger' : 'success'">
            {{ hub.delivery_disabled ? '🛇' : '' }}
            {{ hub.name }}
          </CBadge>
        </td>
      </template>

      <!-- Is Working -->
      <template #is_working="{ item, index }">
        <td class="text-center pr-4">
          <!-- Working now -->
          <template v-if="item.status?.is_working_now">
            <!-- On break -->
            <template v-if="item.status?.on_break">
              <CSpinner v-if="item.updating" size="sm" color="warning" class="mb-1" />
              <span v-else class="dot bg-warning" role="button" @click="forceEndBreak(item, index)"></span>
            </template>

            <!-- Not on break -->
            <template v-else>
              <!-- Per Order Contract Active -->
              <template v-if="item.active_contract?.contract_model === 'per_order'">
                <CSpinner v-if="item.updating" size="sm" color="success" class="mb-1" />
                <span v-else class="dot bg-success" role="button" @click="endWorkingStatus(item, index)"></span>
              </template>

              <CLink v-else :to="{ name: 'Edit Courier', params: { id: item.id }, query: { tab: 4 } }">
                <span class="dot bg-success"></span>
              </CLink>
            </template>
          </template>

          <!-- Not working now -->
          <template v-else>
            <!-- Per Order Contract Active -->
            <template v-if="item.active_contract?.contract_model === 'per_order'">
              <CSpinner v-if="item.updating" size="sm" color="danger" class="mb-1" />
              <span v-else class="dot bg-danger" role="button" @click="startWorkingStatus(item, index)"></span>
            </template>

            <CLink v-else :to="{ name: 'Edit Courier', params: { id: item.id }, query: { tab: 4 } }">
              <span class="dot bg-danger"></span>
            </CLink>
          </template>
        </td>
      </template>

      <!-- On Order -->
      <template #on_order="{ item }">
        <td>
          <div class="vertical-center separate-items">
            <!-- COURIER -->
            <div v-if="item.status?.on_order" class="vertical-center justify-content-center separate-items pl-0">
              <div
                v-for="(order, index) in item.active_orders"
                :key="index"
                class="vertical-center justify-content-center flex-gap-1"
              >
                <CLink
                  :to="{ name: 'Order Details', params: { id: order.id } }"
                  v-c-tooltip="{ content: `o#${order.id}`, placement: 'top' }"
                  style="height: 20px"
                >
                  <span class="dot bg-success"></span>
                </CLink>

                <CLink
                  v-if="order.revocable"
                  class="align-self-baseline"
                  v-c-tooltip="{ content: 'Transfer or revoke order', placement: 'top' }"
                  @click="transferOrRevokeOrder(order)"
                >
                  <CIcon name="cil-transfer" />
                </CLink>
              </div>
            </div>
            <div class="vertical-center" v-else><span class="dot"></span></div>
          </div>
        </td>
      </template>

      <!-- Created -->
      <template #created_at="{ item }">
        <td>{{ moment(item.detail?.created_at).format('LL') }}</td>
      </template>

      <!-- Delivery Stats -->
      <template #delivery_stats="{ item }">
        <td>
          <div class="d-flex flex-column">
            <div class="small">
              <CIcon name="cil-clock" class="mr-1" />
              Last Delivery: {{ item.delivery_stats?.last_delivery_date ? moment(item.delivery_stats.last_delivery_date).format('MMM DD, YYYY HH:mm') : 'No deliveries yet' }}
            </div>
            <div class="small">
              <CIcon name="cil-calendar" class="mr-1" />
              Last 2 Weeks: {{ item.delivery_stats?.last_two_weeks_count || 0 }} deliveries
            </div>
            <div class="small">
              <CIcon name="cil-chart" class="mr-1" />
              Total: {{ item.delivery_stats?.total_delivered_count || 0 }} deliveries
            </div>
            <div class="small" v-if="item.status?.is_working_now && item.delivery_stats?.online_time">
              <CIcon name="cil-check-circle" class="mr-1 text-success" />
              Online since: {{ moment(item.delivery_stats.online_time).format('MMM DD, YYYY HH:mm') }}
            </div>
            <div class="small" v-else-if="item.delivery_stats?.last_offline_time">
              <CIcon name="cil-x-circle" class="mr-1 text-danger" />
              Last offline: {{ moment(item.delivery_stats.last_offline_time).format('MMM DD, YYYY HH:mm') }}
            </div>
            <div class="small" v-if="item.delivery_stats?.start_time_not_confirmed">
              <CIcon name="cil-warning" class="mr-1 text-warning" />
              <span class="text-warning">Start time not confirmed</span>
            </div>
          </div>
        </td>
      </template>
    </CDataTable>

    <!-- Actions -->
    <CCard class="actions sticky-bottom">
      <CCardBody class="p-2">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
          <div>
            <h5 class="mt-1">
              Total: <span class="d-inline count bg-primary pb-1">{{ total }}</span>
            </h5>
          </div>

          <div v-if="pages > 1" class="ml-auto">
            <CPagination align="center" :dots="false" :pages="pages" :active-page.sync="activePage" />
          </div>
        </div>
      </CCardBody>
    </CCard>

    <mc-spinner :opacity="0.8" v-show="loading" :mtop="32 + mcSpinnerMarginTop + 'px'" />

    <TransferOrRevokeOrder
      :courierId="selectedOrder?.courier_id"
      :order="
        $globalFunc.extractKeys(selectedOrder, [
          'id',
          'roDesiredDelivireyTime',
          'roAproxDeliveryDateToCourier',
          'roAddress',
          'roCity',
          'roPostal_code',
          'roCountryId',
        ])
      "
      :restaurant="$globalFunc.extractKeys(selectedOrder?.restaurant, ['id', 'restaurant_postcode'])"
      @transferred="getAllItems"
    />

    <CourierInternalNotes :courier="selectedCourier" @close="selectedCourier = null" />
  </CCardBody>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import _debounce from 'lodash/debounce';
import TransferOrRevokeOrder from '@/components/Courier/TransferOrRevokeOrder';
import CourierInternalNotes from '@/views/Courier/Dashboard/CourierInternalNotes';
import CourierLocationsMap from '@/components/Courier/CourierLocationsMap';

export default {
  name: 'BackendTable',
  components: {
    TransferOrRevokeOrder,
    CourierInternalNotes,
    CourierLocationsMap,
  },
  data() {
    return {
      loadedItems: [],
      fields: [
        { key: 'actions', label: '', sorter: false, _style: 'min-width: 120px; width: 120px;' },
        { key: 'id', label: 'ID', _style: 'min-width: 60px; width: 60px;' },
        { key: 'image_icon', label: '', sorter: false, _style: 'min-width: 60px; width: 60px;' },
        { key: 'first_name', label: 'Name', sorter: false, _style: 'min-width: 200px;' },
        // { key: "contact", sorter: false, _style : "min-width: 200px;" },
        { key: 'contract_model', sorter: false, _style: 'min-width: 160px;width: 200px;' },
        { key: 'vehicle', sorter: false, _style: 'min-width: 160px;width: 200px;' },
        { key: 'hubs', label: 'Hubs', sorter: false, _style: 'min-width: 160px;' },
        {
          key: 'is_working',
          label: 'Is Working',
          sorter: false,
          _style: 'min-width: 60px; width: 60px; text-align: center;',
        },
        { key: 'on_order', sorter: false, _style: 'min-width: 60px; width: 60px;' },
        { key: 'delivery_stats', label: 'Delivery Statistics', sorter: false, _style: 'min-width: 220px;' },
        { key: 'created_at', sorter: false, _style: 'min-width: 140px; width: 140px;' },
      ],

      activePage: 1,
      pages: 1,
      total: 0,
      items_per_page: null,
      orderBy: null,
      direction: null,
      search: null,

      hubs: [],
      selectedRegChannel: [],
      selectedOrder: null,
      selectedCourier: null,
      showMap: false,
      mapCouriers: [],
      mapLoading: false,
      locationRefreshTimer: null,
      loading: false,

      // Element Cover Spinner
      mcSpinnerMarginTop: 0,

      // Registration Notes Filter
      regNotesFilter: {
        selectedCity: null,
        selectedShift: null,
        isFlexHours: null,
        selectedVehicle: null,
      },
    };
  },

  beforeMount() {
    this.searchFilter = _debounce(() => {
      this.getAllItems();
    }, 1000);
  },

  async mounted() {
    this.activePage = this.queryPage;

    await Promise.all([this.getAllItems(), this.getHubs(), this.fetchTravelModes()]);

    window.onscroll = () => {
      this.mcSpinnerMarginTop = document.documentElement.scrollTop;
    };
  },

  watch: {
    reloadParams() {
      if (this.queryPage != this.activePage) this.$router.push({ name: 'Couriers', query: { page: this.activePage } });

      this.onTableChange();
    },
  },

  beforeDestroy() {
    this.stopLocationRefresh();
  },

  computed: {
    ...mapGetters(['errors']),
    ...mapGetters('settings', ['getTravelModes']),
    ...mapState({
      courierFilters: (state) => state.filters.deliveryService.couriers,
    }),
    selectedHubs: {
      get() {
        return this.courierFilters.selectedHubs;
      },
      set(value) {
        this.$store.commit('setDeliveryServiceFilter', { key: 'couriers.selectedHubs', value });
      },
    },
    selectedStatus: {
      get() {
        return this.courierFilters.selectedStatus;
      },
      set(value) {
        this.$store.commit('setDeliveryServiceFilter', { key: 'couriers.selectedStatus', value });
      },
    },
    selectedContractModel: {
      get() {
        return this.courierFilters.selectedContractModel;
      },
      set(value) {
        this.$store.commit('setDeliveryServiceFilter', { key: 'couriers.selectedContractModel', value });
      },
    },
    selectedEmploymentType: {
      get() {
        return this.courierFilters.selectedEmploymentType;
      },
      set(value) {
        this.$store.commit('setDeliveryServiceFilter', { key: 'couriers.selectedEmploymentType', value });
      },
    },
    selectedVehicleMode: {
      get() {
        return this.courierFilters.selectedVehicleMode;
      },
      set(value) {
        this.$store.commit('setDeliveryServiceFilter', { key: 'couriers.selectedVehicleMode', value });
      },
    },
    itemsPerPage: {
      get: function () {
        return this.items_per_page ? this.items_per_page : parseInt(process.env.VUE_APP_ITEMS_PER_PAGE);
      },
      set: function (newValue) {
        this.items_per_page = newValue;
      },
    },
    reloadParams() {
      return [this.activePage];
    },
    queryPage: {
      get() {
        return parseInt(this.$route.query.page) || 1;
      },
      set(value) {
        this.$router.push({ name: 'Couriers', query: { page: value } });
      },
    },
    employmentTypeOptions() {
      const model = this.selectedContractModel;
      const baseOptions = [{ value: 'self_employed', label: 'Self Employed' }];

      if (model === 'per_order') {
        return [{ value: 'p2p', label: 'P2P' }, ...baseOptions];
      } else if (model === 'hourly') {
        return [{ value: 'employee', label: 'Employee' }, ...baseOptions];
      }

      return [];
    },
  },

  methods: {
    ...mapActions('settings', ['fetchTravelModes']),

    async getAllItems() {
      this.loading = true;

      var url = new URL(this.$backend.COURIER.COURIERS.GET_ALL),
        params = {
          page: this.activePage,
          itemsPerPage: this.itemsPerPage,
          'order-by': this.orderBy,
          direction: this.direction,
          hubs: this.selectedHubs,
          status: this.selectedStatus,
          selfreg: this.selectedRegChannel,
          search: this.search,
          'with-trashed': true,
          'contract-model': this.selectedContractModel,
          'employment-type': this.selectedEmploymentType,
          'vehicle-mode': this.selectedVehicleMode,
          'registration-notes': JSON.stringify(this.regNotesFilter),
        };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== 'undefined' &&
          params[key] !== null &&
          params[key] !== '' &&
          params[key].length !== 0
        )
          url.searchParams.append(key, params[key]);
      });

      await this.$axios
        .get(url)
        .then((response) => {
          return response.data;
        })
        .then((items) => {
          // Son sayfadan daha büyük bir queryPage çağrımı (?page=XXX) yapılırsa;
          if (items.meta.current_page > items.meta.last_page) this.activePage = 1;
          else {
            this.activePage = items.meta.current_page;
            this.pages = items.meta.last_page;
            this.total = items.meta.total;

            this.loadedItems = items.data.map((el) => {
              return {
                ...el,
                updating: false,
                deleting: false,
                _classes: !el.detail ? 'strikeout disable-item ' : el.hubs.length === 0 ? 'table-warning ' : '',
              };
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onTableChange() {
      // Active Page güncellendiğinde çağrılıyor.
      this.getAllItems();
    },
    paginationChange(value) {
      // Items Per Page güncellendiğinde çağrılıyor.
      this.itemsPerPage = value;
      this.getAllItems();
    },
    sorterValue(item) {
      this.orderBy = item.column;
      this.direction = item.column ? (item.asc ? 'asc' : 'desc') : null;
      this.getAllItems();
    },
    async deleteItem(item, index) {
      const confirm = await this.mSwal.fire({
        title: 'Are you sure you want to delete?',
        text: 'The courier will be deleted!',
        icon: 'warning',
      });

      if (confirm.isConfirmed) {
        const deleteUrl = this.$backend.COURIER.COURIERS.DELETE.replace('{id}', item.id);
        item.deleting = true;

        try {
          const { data } = await this.$axios.delete(deleteUrl);
          // index !== -1 && this.$delete(this.loadedItems, index);
          item._classes = index !== -1 && 'strikeout disable-item';
          item.is_deletable = item.status.is_working_now = item.deleting = false;
          item.vehicle = null;
          this.$toast.success(data.data.message);
        } catch (error) {
          item.deleting = false;

          if (error.response) {
            this.$toast.error(`${error.response.data.message}`);
          } else if (error.request) {
            this.$toast.error('No response received from the server.');
          } else {
            this.$toast.error('An unexpected error occurred.');
          }
        } finally {
          this.$forceUpdate();
        }
      }
    },

    async getHubs() {
      await this.$axios
        .get(this.$backend.COURIER.HUBS.GET_LIST)
        .then(({ data }) => (this.hubs = data))
        .catch(() => (this.hubs = []));
    },
    // Filters
    inputFilter: _debounce(function () {
      this.getAllItems();
      if (this.showMap) {
        this.loadCourierLocations();
      }
    }, 300),
    handleContractModelChange() {
      this.selectedEmploymentType = null;
      this.getAllItems();
      if (this.showMap) {
        this.loadCourierLocations();
      }
    },

    async toggleMap() {
      this.showMap = !this.showMap;

      if (this.showMap) {
        await this.loadCourierLocations();
        this.startLocationRefresh();
      } else {
        this.stopLocationRefresh();
      }
    },

    async loadCourierLocations() {
      this.mapLoading = true;

      var url = new URL(this.$backend.COURIER.COURIERS.LOCATIONS),
        params = {
          hubs: this.selectedHubs,
          status: this.selectedStatus,
          'contract-model': this.selectedContractModel,
          'employment-type': this.selectedEmploymentType,
          'vehicle-mode': this.selectedVehicleMode,
        };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== 'undefined' &&
          params[key] !== null &&
          params[key] !== '' &&
          params[key].length !== 0
        )
          url.searchParams.append(key, params[key]);
      });

      await this.$axios
        .get(url)
        .then(({ data }) => {
          this.mapCouriers = data;
        })
        .catch(() => {
          this.mapCouriers = [];
        })
        .finally(() => {
          this.mapLoading = false;
        });
    },

    startLocationRefresh() {
      this.stopLocationRefresh();
      this.locationRefreshTimer = setInterval(this.loadCourierLocations, 60000);
    },

    stopLocationRefresh() {
      if (this.locationRefreshTimer) {
        clearInterval(this.locationRefreshTimer);
        this.locationRefreshTimer = null;
      }
    },

    resetFilters() {
      if (this.$store.state.filterShow) {
        this.selectedHubs = [];
        this.selectedStatus = [];
        this.selectedRegChannel = [];
        this.selectedContractModel = null;
        this.selectedEmploymentType = null;
        this.selectedVehicleMode = null;
        this.search = '';
        this.regNotesFilter = {
          selectedCity: null,
          selectedShift: null,
          isFlexHours: null,
          selectedVehicle: null,
        };
        this.getAllItems();
        if (this.showMap) {
          this.loadCourierLocations();
        }
      }
      this.$store.commit('toggle', 'filterShow');
    },

    transferOrRevokeOrder(order) {
      this.selectedOrder = order;
      this.$store.state.transferOrRevokeOrderShow = true;
    },

    showInternalNotes(courier) {
      this.selectedCourier = courier;
      this.$store.commit('set', ['courierInternalNotesAsideShow', true]);
    },

    async forceEndBreak(courier, index) {
      const confirm = await this.mSwal.fire({
        title: "Are you sure you want to end the courier's break?",
        text: 'The courier will be taken off break status.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, end break',
        cancelButtonText: 'No, keep on break',
      });

      if (confirm.isConfirmed) {
        const endBreakUrl = this.$backend.COURIER.BREAKS.FORCE_END.replace('{id}', courier.id);
        courier.updating = true;

        try {
          const { data } = await this.$axios.post(endBreakUrl);

          if (data.success) {
            courier.status.on_break = false;
            this.$toast.success("The courier's break has been ended successfully.");
          }
        } catch (error) {
          const errorMessage = typeof this.errors === 'object' ? Object.values(this.errors).join() : this.errors;
          this.$toast.error(errorMessage);
        } finally {
          courier.updating = false;
          this.$forceUpdate();
        }
      }
    },

    async endWorkingStatus(courier, index) {
      const confirm = await this.mSwal.fire({
        title: "Are you sure you want to end the courier's working status?",
        text: 'The courier will be marked as not working.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, end working status',
        cancelButtonText: 'No, keep working',
      });

      if (confirm.isConfirmed) {
        const endWorkingUrl = this.$backend.COURIER.WORKING_HOURS.END.replace('{id}', courier.id);
        courier.updating = true;

        try {
          const { data } = await this.$axios.post(endWorkingUrl);

          if (data.success) {
            courier.status.is_working_now = false;
            this.$toast.success("The courier's working status has been ended successfully.");
          }
        } catch (error) {
          const errorMessage = typeof this.errors === 'object' ? Object.values(this.errors).join() : this.errors;
          this.$toast.error(errorMessage);
        } finally {
          courier.updating = false;
          this.$forceUpdate();
        }
      }
    },

    async startWorkingStatus(courier, index) {
      const confirm = await this.mSwal.fire({
        title: "Are you sure you want to start the courier's working status?",
        text: 'The courier will be marked as working.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, start working',
        cancelButtonText: 'No, keep not working',
      });

      if (confirm.isConfirmed) {
        const startWorkingUrl = this.$backend.COURIER.WORKING_HOURS.START.replace('{id}', courier.id);
        courier.updating = true;

        try {
          const { data } = await this.$axios.post(startWorkingUrl);

          if (data.success) {
            courier.status.is_working_now = true;
            this.$toast.success("The courier's working status has been started successfully.");
          }
        } catch (error) {
          const errorMessage = typeof this.errors === 'object' ? Object.values(this.errors).join() : this.errors;
          this.$toast.error(errorMessage);
        } finally {
          courier.updating = false;
          this.$forceUpdate();
        }
      }
    },
  },
};
</script>

<style>
.table th,
.table td {
  padding: 0.5rem 0.25rem;
  vertical-align: middle;
}

.search input {
  font-size: 1.0375rem !important;
}

.c-legend-title {
  font-weight: 500;
  font-size: 1rem;
  border-radius: 0.375rem;
}
</style>
